import React, { useEffect, useState, useCallback, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import Joi from 'joi';
import { Spinner, DialogComponent } from '../../../../Components';
import { useSuccessDialog } from '../../../../SharedComponents';
import {
  getMessageFromErrorResponse,
  showError,
  showSuccess,
} from '../../../../Helper';
import {
  VendorTypes,
  AddAlreadyExisting,
  AddNewVendor,
  ContactComponent,
} from '.';
import {
  CreateVendorAPI,
  GetLookupItemsByLookupTypeName,
} from '../../../../Services';

const parentTranslationPath = 'Vendors';
const translationPath = '';

const defaults = {
  defaultState: {
    vendorName: null,
    landLineNumber: null,
    languageId: null,
    email: null,
    cityId: null,
    countryId: null,
    contactId: null,
  },
  defaultSelect: {
    vendorName: null,
    landLineNumber: null,
    language: null,
    city: null,
    country: null,
  },
  defaultNewVendorState: {
    email: null,
    vendorName: null,
    landlineNumber: null,
    languageId: null,
    countryId: null,
    cityId: null,
    multiPropertyOwner: false,
    promoBy_SMS_Email: true,
    contactClassification: null,
  },
  defaultNewVendorSelect: {
    vendorName: null,
    landLineNumber: null,
    language: null,
    city: null,
    country: null,
  },
  defaultHelperText: {
    landlineNumber: '',
    vendorName: '',
    language: '',
    country: '',
    city: '',
  },
};
const reducer = (select, action) => {
  if (action.id !== 'edit') return { ...select, [action.id]: action.value };
  if (action.id === 'edit') {
    return {
      ...action.value,
    };
  }
};
export const AddVendorComponent = ({ isOpen, onClose, onSave, formId }) => {
  const { t } = useTranslation(parentTranslationPath);
  const { showDialog } = useSuccessDialog();
  const [contactClassifications, setContactClassifications] = useState(null);

  const [isLoading, setIsLoading] = useState({
    allContacts: false,
    saveVendor: false,
    language: false,
    country: false,
    city: false,
    checkDuplicationLandlineNumber: false,
  });

  const [helperText, setHelperText] = useState({
    ...defaults.defaultHelperText,
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectVendorType, setSelectVendorType] = useState('');
  const [state, setState] = useReducer(reducer, { ...defaults.defaultState });

  const [selected, setSelected] = useReducer(reducer, {
    vendorName: null,
    landLineNumber: null,
    language: null,
    city: null,
    country: null,
  });
  const [newVendorWithContactState, setNewVendorWithContactState] = useReducer(
    reducer,
    { ...defaults.defaultNewVendorState }
  );

  const [selectedNewVendorWithContact, setSelectedNewVendorWithContact] =
    useReducer(reducer, { ...defaults.defaultNewVendorSelect });

  const schema = Joi.object({
    vendorName: Joi.string()
      .required()
      .messages({
        'string.base': t(`vendor-name-is-required`),
        'string.empty': t(`vendor-name-is-required`),
        'string.required': t(`vendor-name-is-required`),
      }),
  })
    .options({
      abortEarly: false,
      allowUnknown: true,
    })
    .validate(state);

  const schemaNewVendor = Joi.object({
    vendorName: Joi.string()
      .required()
      .messages({
        'string.base': t(`vendor-name-is-required`),
        'string.empty': t(`vendor-name-is-required`),
        'string.required': t(`vendor-name-is-required`),
      }),
    landlineNumber: Joi.string()
      .required()
      .messages({
        'string.base': t(`contact-number-is-required`),
        'string.empty': t(`contact-number-is-required`),
        'string.required': t(`contact-number-is-required`),
      }),
    languageId: Joi.number()
      .required()
      .messages({
        'number.base': t(`language-name-is-required`),
        'number.empty': t(`language-name-is-required`),
        'number.required': t(`language-name-is-required`),
      }),
    countryId: Joi.number()
      .required()
      .messages({
        'number.base': t(`country-is-required`),
        'number.empty': t(`country-is-required`),
        'number.required': t(`country-is-required`),
      }),
    cityId: Joi.number()
      .required()
      .messages({
        'number.base': t(`city-is-required`),
        'number.empty': t(`city-name-is-required`),
        'number.required': t(`city-name-is-required`),
      }),
  })
    .options({
      abortEarly: false,
      allowUnknown: true,
    })
    .validate(newVendorWithContactState);

  const saveHandler = useCallback(async () => {
    if (
      (selectVendorType === 'new' && schemaNewVendor.error) ||
      (selectVendorType === 'existing' && schema.error)
    ) {
      showError(t(`fill-all-required-fields`));
      return;
    }
    if (Object.values(helperText).some((s) => s !== '')) {
      showError(t(`fill-fields-with-correct-values`));
      return;
    }
    setIsLoading((item) => ({ ...item, saveVendor: true }));
    const result =
      selectVendorType === 'new'
        ? await CreateVendorAPI(false, {
            ...newVendorWithContactState,
            contactClassification: contactClassifications?.lookupItemId || null,
          })
        : await CreateVendorAPI(true, {
            ...state,
          });

    setIsLoading((item) => ({ ...item, saveVendor: false }));

    if (
      !(result && result.status && result.status == 200) &&
      !result?.Message
    ) {
      showSuccess(t(`add-vendor-succssfuly`));
      showDialog(
        t('GlobalSuccessDialog:add-vendor-successfully'),
        {
          name: result?.vendorName,
          id: result?.vendorId,
          type: 'vendor',
          formId: formId,
        },
        true
      );
      onSave(result);
    } else if (result && result.Message) {
      const messageOne = getMessageFromErrorResponse(result).replaceAll(
        '___',
        ''
      );

      showError(
        t(
          messageOne?.includes('Duplicated') ||
            messageOne.includes('ASSOCIAITED')
            ? messageOne
            : 'add-vendor-faild'
        )
      );
    } else showError(t(`add-vendor-faild`));
  }, [
    state,
    selectVendorType,
    newVendorWithContactState,
    schemaNewVendor,
    schema,
    contactClassifications,
  ]);

  const clearData = async () => {
    setHelperText({ ...defaults.defaultHelperText });
    setState({ ...defaults.defaultState });
    setSelected({ ...defaults.defaultSelect });
    setSelectedNewVendorWithContact({
      ...defaults.defaultNewVendorSelect,
    });
    setNewVendorWithContactState({ ...defaults.defaultNewVendorState });
  };

  const getLookupItemsByName = async (lookUpName) => {
    const result = await GetLookupItemsByLookupTypeName({
      lookUpName,
      pageIndex: 1,
      pageSize: 300,
    });
    if (!(result && result.status && result.status !== 200)) {
      if (result && result.result)
        setContactClassifications(
          result.result.find(
            (el) => el?.lookupItemName.trim() === 'PSI Partner'
          )
        );
    } else setContactClassifications(null);
  };

  useEffect(() => {
    if (!isOpen) {
      clearData();
    }
  }, [isOpen]);

  useEffect(() => {
    getLookupItemsByName('contactClassifications');
  }, []);

  return (
    <>
      <DialogComponent
        saveText={t(`save`)}
        disableBackdropClick
        maxWidth='sm'
        titleText={t(`add-vendor`)}
        saveClasses={'btns theme-solid bg-primary'}
        saveType='button'
        dialogContent={
          <div className='w-100  d-flex-column-center'>
            <Spinner isActive={isLoading.saveVendor} isAbsolute />
            <div className='w-100 d-flex-column'>
              <div className='vendor-dialog-wrapper'>
                <div className='dialog-content-wrapper'>
                  {selectVendorType === 'existing' && selected?.vendorName && (
                    <div className='w-35'>
                      <AddAlreadyExisting
                        translationPath={translationPath}
                        parentTranslationPath={parentTranslationPath}
                        selected={selected}
                      />
                    </div>
                  )}
                  <div
                    className={
                      (selectVendorType === 'existing' && 'w-65 px-2') ||
                      'w-100'
                    }
                  >
                    <VendorTypes
                      parentTranslationPath={parentTranslationPath}
                      translationPath={translationPath}
                      selectVendorType={selectVendorType}
                      setSelectVendorType={setSelectVendorType}
                      clearData={clearData}
                    />
                    {selectVendorType === 'existing' && (
                      <div className='px-3 d-flex-column-center mb-2 mt-4'>
                        <ContactComponent
                          translationPath={translationPath}
                          parentTranslationPath={parentTranslationPath}
                          selected={selected}
                          state={state}
                          setSelected={setSelected}
                          setState={setState}
                          contactClassifications={contactClassifications}
                        />
                      </div>
                    )}
                    {selectVendorType === 'new' && (
                      <div className='d-flex-column-center w-100 mb-2 mt-2'>
                        <AddNewVendor
                          translationPath={translationPath}
                          parentTranslationPath={parentTranslationPath}
                          dynamicComponentProps={{
                            selectedNewVendorWithContact,
                            newVendorWithContactState,
                            setSelectedNewVendorWithContact,
                            setNewVendorWithContactState,
                            setIsLoading,
                            schema: schemaNewVendor,
                            isSubmitted,
                            setIsLoading,
                            isLoading,
                            helperText,
                            setHelperText,
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
        isOpen={isOpen}
        saveIsDisabled={
          isLoading.saveVendor ||
          (selectVendorType === 'new' &&
            (schemaNewVendor.error ||
              Object.values(helperText).some((h) => h !== ''))) ||
          (selectVendorType === 'existing' && schema.error) ||
          isLoading.checkDuplicationLandlineNumber ||
          !selectVendorType
        }
        onSaveClicked={() => saveHandler()}
        onCancelClicked={onClose}
        translationPath={translationPath}
        parentTranslationPath={parentTranslationPath}
        onCloseClicked={onClose}
      />
    </>
  );
};
